import {
  ChevronRight,
  MoreVertical, Plus, Search,
  Users
} from 'lucide-react';
import React, {
  useCallback, useContext, useState
} from 'react';
import { Link } from 'react-router-dom';

import { ModalComponent } from '../../base2';
import {
  AuthUserContext,
  Button, NewPersonFormComponent, ScreenHeader
} from '../../components';
import { UserIdentityProvider } from '../../context';
import PeoplePageContent from '../../demo-components/people';
import { useIndex } from '../../hooks';

import './style.scss';

export function PeoplePage() {
  return (
    <UserIdentityProvider>
      <PeopleComponent />
    </UserIdentityProvider>
  );
}

function _PeopleComponent() {
  const { user } = React.useContext(AuthUserContext);

  const { data, loading, update } = useIndex('people', { user_id: user?.id });

  const [ showModal, setShowModal ] = useState(false);

  const openModal = useCallback(() => {
    console.log('open modal');
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleSaveCompletion = useCallback(() => {
    update();
    handleClose();
  }, [ handleClose, update ]);

  if (loading) {
    return <div>Loading people...</div>;
  }

  console.log('People:', user, data);

  return (
    <div className="people-page">
      {/* <h1>People</h1>
      <h3>Manage your contacts and loved ones.</h3>
      <p>
        Add and manage the people in your life. People you add here can be listed as executors or
        beneficiaries in your life documents. With premium you can even create life documents for
        them too!
      </p>

      <div>
        {people.map((person) => (
          <PersonInfoComponent person={person} key={person.id} />
        ))}
      </div> */}
      <PeoplePageContent />

      <div className="button-container" style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={openModal}>Add someone</Button>
        <ModalComponent
          closeModal={handleClose}
          contentStyle={{ height: '60%', width: '96%' }}
          showModal={showModal}
        >
          <NewPersonFormComponent handleSaveCompletion={handleSaveCompletion} />
        </ModalComponent>
      </div>
    </div>
  );
}

function getFullName(person) {
  return [ person.firstName, person.middleName, person.lastName ].filter(Boolean).join(' ');
}

function getInitials(person) {
  return [ person.firstName, person.middleName, person.lastName ].filter(Boolean).map(name => name[0]).join('');
}

export function PeopleComponent() {
  const { user } = useContext(AuthUserContext);

  const [ searchTerm, setSearchTerm ] = useState('');
  const [ filterBy, setFilterBy ] = useState('all');
  const [ showAddMenu, setShowAddMenu ] = useState(false);

  const { data, loading, update } = useIndex('people', { user_id: user?.id });

  const people = data?.map(person => ({
    ...person,
    name: getFullName(person),
    initials: getInitials(person),
    completion: 50
  }));

  const filteredPeople = people.filter(person => {
    const matchesSearch = person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         person.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterBy === 'all' || person.role.toLowerCase().includes(filterBy.toLowerCase());
    return matchesSearch && matchesFilter;
  });

  const getCompletionColor = (completion) => {
    if (completion >= 75) return '#10b981';
    if (completion >= 50) return '#3b82f6';
    if (completion >= 25) return '#f59e0b';
    return '#ef4444';
  };

  const handleAddPerson = useCallback(() => {
    console.log('handleAddPerson');
    setShowAddMenu(true);
  }, []);

  const handleCloseAddMenu = useCallback(() => {
    console.log('handleCloseAddMenu');
    setShowAddMenu(false);
  }, []);

  const handleSaveCompletion = useCallback(() => {
    console.log('handleSaveCompletion');
    setShowAddMenu(false);
  }, []);

  return (
    <main className="people-component screen">
      {/* Page Header */}

      <ScreenHeader
        title="People"
        description="Manage your contacts and loved ones."
      />
          
      {/* <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">3</div>
          <div className="stat-label">Total People</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">1</div>
          <div className="stat-label">Executors</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">1</div>
          <div className="stat-label">Beneficiaries</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">50%</div>
          <div className="stat-label">Avg Complete</div>
        </div>
      </div> */}

      {/* Controls */}
      <div className="controls-section">
        <div className="search-filter-group" style={{ display: 'none' }}>
          <div className="search-container">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search people..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="filter-select"
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="executor">Executors</option>
            <option value="beneficiary">Beneficiaries</option>
            <option value="healthcare">Healthcare Agents</option>
          </select>
        </div>
        <button className="add-button" onClick={handleAddPerson}>
          <Plus size={20} />
            Add Person
        </button>
      </div>

      {/* People List */}
      <div className="people-grid">
        {filteredPeople.length > 0 ? (
          filteredPeople.map((person) => (
            <div key={person.id} className="person-card">
              <div className="person-header">
                <div className="person-main">
                  <div className="avatar">{person.initials}</div>
                  <div className="person-info">
                    <h3 className="person-name">{person.name}</h3>
                    {
                      person.relationship && (
                        <p className="person-relationship">{person.relationship}</p>
                      )
                    }
                    {
                      person.role && (
                        <span 
                          className="role-badge"
                          style={{ 
                            backgroundColor: `${person.roleColor}20`,
                            color: person.roleColor 
                          }}
                        >
                          {person.role}
                        </span>
                      )}
                  </div>
                </div>
                <div className="person-actions">
                  <button className="action-button">
                    <MoreVertical size={20} />
                  </button>
                  <button className="action-button">
                    <Link to={`/people/${person.id}`}>
                      <ChevronRight size={20} />
                    </Link>
                  </button>
                </div>
              </div>
                
              <div className="person-details">
                <div className="contact-info">
                  <div className="contact-item">{person.contactEmail}</div>
                  <div className="contact-item">{person.contactPhone}</div>
                </div>
                <div className="progress-section">
                  {
                    person.completion === 100 
                      ? (
                        <div className="progress-label">Profile Complete</div>
                      ) : (
                        <div 
                          className="progress-value"
                          style={{ color: getCompletionColor(person.completion) }}
                        >
                          {person.completion}%
                        </div>
                      )
                  }

                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${person.completion}%`,
                        backgroundColor: getCompletionColor(person.completion)
                      }}
                    />
                  </div>
                  {/* <div className="last-updated">Updated {person.lastUpdated}</div> */}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <Users className="empty-icon" />
            <h3 className="empty-title">No people found</h3>
            <p className="empty-description">
              {searchTerm ? `No results for "${searchTerm}"` : 'Start by adding your first contact'}
            </p>
            <button className="add-button">
              <Plus size={20} />
                Add Your First Person
            </button>
          </div>
        )}
      </div>

      <ModalComponent
        closeModal={handleCloseAddMenu}
        contentStyle={{ height: '60%', width: '96%' }}
        showModal={showAddMenu}
      >
        <NewPersonFormComponent handleSaveCompletion={handleSaveCompletion} />
      </ModalComponent>
    </main>
  );
}